// A very small .xlsx writer, with no dependency.
//
// Why not SheetJS
// ---------------
// One month-end export does not justify adding a spreadsheet library to a
// cockpit bundle, and the workbook this produces is deliberately plain: a
// header row, a label column and numbers. What follows is the OOXML minimum
// that Excel, Numbers, LibreOffice and Google Sheets all open.
//
// The zip entries are STORED, not deflated. An xlsx is a zip and the format
// permits stored entries, so this avoids shipping a compressor. A 72-column
// month is roughly 300 KB uncompressed, which is a download, not a problem.
// If these ever get large, CompressionStream('deflate-raw') is the upgrade
// path and only the entry writer below changes.

export type XlsxCell = string | number | null

export type XlsxSheet = {
  name: string
  // Row 0 is the top row. A null cell is written as blank, NOT as zero: the
  // difference between "no maintenance this month" and "R0.00 of maintenance"
  // is a difference an owner would notice.
  rows: XlsxCell[][]
  // Zero-based row indexes to render bold, e.g. headers and subtotals.
  boldRows?: number[]
  // Zero-based row indexes whose numbers are counts, not rands, so they are
  // formatted without decimals.
  countRows?: number[]
  // Column widths in characters, applied left to right.
  columnWidths?: number[]
  // Rows to freeze at the top and columns to freeze at the left.
  freezeRows?: number
  freezeCols?: number
}

// -- XML -------------------------------------------------------------------

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Control characters are not legal in XML 1.0 and would make the file
    // unopenable rather than merely ugly.
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
}

function columnName(index: number): string {
  let n = index + 1
  let name = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    name = String.fromCharCode(65 + rem) + name
    n = Math.floor((n - 1) / 26)
  }
  return name
}

// Style indexes into cellXfs below.
const STYLE_DEFAULT = 0
const STYLE_BOLD = 1
const STYLE_MONEY = 2
const STYLE_MONEY_BOLD = 3
const STYLE_COUNT = 4
const STYLE_COUNT_BOLD = 5

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2">
<numFmt numFmtId="164" formatCode="#,##0.00;[Red](#,##0.00)"/>
<numFmt numFmtId="165" formatCode="#,##0;[Red](#,##0)"/>
</numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="6">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="164" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`

function sheetXml(sheet: XlsxSheet): string {
  const bold = new Set(sheet.boldRows ?? [])
  const counts = new Set(sheet.countRows ?? [])

  const cols = sheet.columnWidths?.length
    ? `<cols>${sheet.columnWidths
        .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
        .join('')}</cols>`
    : ''

  const freezeRows = sheet.freezeRows ?? 0
  const freezeCols = sheet.freezeCols ?? 0
  const pane =
    freezeRows > 0 || freezeCols > 0
      ? `<sheetViews><sheetView workbookViewId="0"><pane xSplit="${freezeCols}" ySplit="${freezeRows}" topLeftCell="${columnName(
          freezeCols,
        )}${freezeRows + 1}" activePane="bottomRight" state="frozen"/></sheetView></sheetViews>`
      : ''

  const rows = sheet.rows
    .map((cells, rowIndex) => {
      const isBold = bold.has(rowIndex)
      const isCount = counts.has(rowIndex)
      const body = cells
        .map((cell, colIndex) => {
          if (cell === null || cell === undefined || cell === '') return ''
          const ref = `${columnName(colIndex)}${rowIndex + 1}`
          if (typeof cell === 'number') {
            if (!Number.isFinite(cell)) return ''
            const style = isCount
              ? isBold
                ? STYLE_COUNT_BOLD
                : STYLE_COUNT
              : isBold
                ? STYLE_MONEY_BOLD
                : STYLE_MONEY
            return `<c r="${ref}" s="${style}"><v>${cell}</v></c>`
          }
          const style = isBold ? STYLE_BOLD : STYLE_DEFAULT
          return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${esc(
            String(cell),
          )}</t></is></c>`
        })
        .join('')
      return `<row r="${rowIndex + 1}">${body}</row>`
    })
    .join('')

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${pane}${cols}<sheetData>${rows}</sheetData></worksheet>`
}

// Excel refuses these characters in a sheet name and caps it at 31 chars.
function safeSheetName(name: string, index: number): string {
  const cleaned = name.replace(/[\\/*?:[\]]/g, ' ').trim()
  const capped = cleaned.slice(0, 31)
  return capped || `Sheet${index + 1}`
}

// -- ZIP -------------------------------------------------------------------

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[i] = c >>> 0
  }
  return table
})()

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

type Entry = { name: string; bytes: Uint8Array; crc: number; offset: number }

function zip(files: { name: string; content: string }[]): Blob {
  const encoder = new TextEncoder()
  const chunks: Uint8Array[] = []
  const entries: Entry[] = []
  let offset = 0

  const push = (bytes: Uint8Array) => {
    chunks.push(bytes)
    offset += bytes.length
  }

  for (const file of files) {
    const nameBytes = encoder.encode(file.name)
    const bytes = encoder.encode(file.content)
    const crc = crc32(bytes)

    const header = new Uint8Array(30 + nameBytes.length)
    const view = new DataView(header.buffer)
    view.setUint32(0, 0x04034b50, true) // local file header
    view.setUint16(4, 20, true) // version needed
    view.setUint16(6, 0x0800, true) // UTF-8 names
    view.setUint16(8, 0, true) // method: stored
    view.setUint16(10, 0, true) // mod time
    view.setUint16(12, 0x2100, true) // mod date, 1 Jan 2016; fixed so exports are reproducible
    view.setUint32(14, crc, true)
    view.setUint32(18, bytes.length, true)
    view.setUint32(22, bytes.length, true)
    view.setUint16(26, nameBytes.length, true)
    view.setUint16(28, 0, true) // extra length
    header.set(nameBytes, 30)

    entries.push({ name: file.name, bytes, crc, offset })
    push(header)
    push(bytes)
  }

  const centralStart = offset
  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name)
    const record = new Uint8Array(46 + nameBytes.length)
    const view = new DataView(record.buffer)
    view.setUint32(0, 0x02014b50, true) // central directory header
    view.setUint16(4, 20, true) // version made by
    view.setUint16(6, 20, true) // version needed
    view.setUint16(8, 0x0800, true)
    view.setUint16(10, 0, true) // stored
    view.setUint16(12, 0, true)
    view.setUint16(14, 0x2100, true)
    view.setUint32(16, entry.crc, true)
    view.setUint32(20, entry.bytes.length, true)
    view.setUint32(24, entry.bytes.length, true)
    view.setUint16(28, nameBytes.length, true)
    view.setUint16(30, 0, true) // extra
    view.setUint16(32, 0, true) // comment
    view.setUint16(34, 0, true) // disk
    view.setUint16(36, 0, true) // internal attrs
    view.setUint32(38, 0, true) // external attrs
    view.setUint32(42, entry.offset, true)
    record.set(nameBytes, 46)
    push(record)
  }

  const end = new Uint8Array(22)
  const endView = new DataView(end.buffer)
  endView.setUint32(0, 0x06054b50, true)
  endView.setUint16(4, 0, true)
  endView.setUint16(6, 0, true)
  endView.setUint16(8, entries.length, true)
  endView.setUint16(10, entries.length, true)
  endView.setUint32(12, offset - centralStart, true)
  endView.setUint32(16, centralStart, true)
  endView.setUint16(20, 0, true)
  push(end)

  // Concatenated into one buffer rather than handed to Blob as an array of
  // views: TypeScript's DOM lib types BlobPart as ArrayBufferView<ArrayBuffer>,
  // and TextEncoder.encode returns ArrayBufferLike, which is not assignable. One
  // allocation is also simply less work than many.
  let total = 0
  for (const chunk of chunks) total += chunk.length
  const merged = new Uint8Array(new ArrayBuffer(total))
  let at = 0
  for (const chunk of chunks) {
    merged.set(chunk, at)
    at += chunk.length
  }

  return new Blob([merged], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

// -- Public ----------------------------------------------------------------

export function buildXlsx(sheets: XlsxSheet[]): Blob {
  const named = sheets.map((s, i) => ({ ...s, name: safeSheetName(s.name, i) }))

  const files: { name: string; content: string }[] = [
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${named
  .map(
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  )
  .join('\n')}
</Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${named
        .map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
        .join('')}</sheets>
</workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${named
  .map(
    (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
  )
  .join('\n')}
<Relationship Id="rId${named.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    { name: 'xl/styles.xml', content: STYLES_XML },
    ...named.map((sheet, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      content: sheetXml(sheet),
    })),
  ]

  return zip(files)
}

/** Builds the workbook and hands it to the browser as a download. */
export function downloadXlsx(filename: string, sheets: XlsxSheet[]): void {
  const blob = buildXlsx(sheets)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Revoked on the next tick so the click has definitely been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
